import {
  blue,
  geekblue,
  green,
  orange,
  red,
  volcano,
} from "@ant-design/colors";
import { Button } from "antd";
import { darken, getLuminance } from "polished";
import styled, { css } from "styled-components";
import { ActionButtonType } from "./ActionButton";
import { Link } from "react-router-dom";

const actionButtonColors: Partial<Record<ActionButtonType, string>> = {
  add: blue[5],
  save: geekblue[5],
  edit: blue[6],
  delete: red[6],
  close: orange[6],
  reset: red[6],
  submit: blue[6],
  cancel: "transparent",
  detail: green[6],
  quickLook: "transparent",
  refresh: "transparent",
  scan: volcano[6],
  history: orange[6],
  showMore: "transparent",
  copy: blue[6],
  download: green[6],
  other: blue[6],
};

const actionButtonBorders: Partial<Record<ActionButtonType, string>> = {
  quickLook: `1px solid`,
};

const getContrastColor = (backgroundColor: string): string | false => {
  if (backgroundColor === "transparent") return false;
  const luminance = getLuminance(backgroundColor);
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};

export const StyledActionLink = styled(Link)<{
  actionType?: string;
  noBg?: boolean;
  disabled?: boolean;
  loading?: boolean;
  color?: string;
  showBorder?: boolean;
  size?: "small" | "medium" | "large";
}>`
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: center;
  border: ${({ actionType, showBorder }) =>
    showBorder
      ? "1px solid"
      : actionButtonBorders[actionType as keyof typeof actionButtonBorders] ||
        "none"}!important;
  border-color: ${({ theme, actionType, noBg, disabled, color }) =>
    disabled
      ? theme.palette.background.default
      : noBg
        ? "transparent"
        : color ||
          actionButtonColors[actionType as keyof typeof actionButtonColors] ||
          theme.palette.text.primary}!important;
  border-radius: 7px !important;
  height: 25px !important;
  min-width: 25px !important;
  width: unset !important;

  ${({ size }) =>
    size === "small" &&
    css`
      height: 20px !important;
      min-width: 20px !important;
    `}
  ${({ size }) =>
    size === "medium" &&
    css`
      height: 25px !important;
      min-width: 25px !important;
    `}
  ${({ size }) =>
    size === "large" &&
    css`
      height: 30px !important;
      min-width: 30px !important;
    `}

  cursor: pointer;
  padding: 5px !important;
  font-size: 13px !important;
  background: unset !important;
  background-color: ${({ theme, actionType, noBg, disabled, color }) =>
    disabled
      ? theme.palette.background.default
      : noBg
        ? "transparent"
        : color ||
          actionButtonColors[actionType as keyof typeof actionButtonColors] ||
          theme.palette.background.default}!important;
  color: ${({ theme, actionType, noBg, color }) =>
    noBg
      ? theme.palette.text.primary
      : getContrastColor(
          color ||
            actionButtonColors[actionType as keyof typeof actionButtonColors] ||
            theme.palette.background.default,
        ) || theme.palette.text.primary}!important;
  ${({ noBg, theme, actionType, color }) =>
    (noBg ||
      !getContrastColor(
        color ||
          actionButtonColors[actionType as keyof typeof actionButtonColors] ||
          theme.palette.background.default,
      )) &&
    css`
      box-shadow: none !important;
    `}

  > svg {
    font-size: ${({ size }) =>
      size === "small" ? "16px" : size === "medium" ? "19px" : "22px"};
  }

  &:hover {
    background-color: ${({ theme, actionType, noBg, color }) =>
      noBg
        ? "transparent"
        : darken(
            0.1,
            color ||
              actionButtonColors[
                actionType as keyof typeof actionButtonColors
              ] ||
              theme.palette.background.default,
          )}!important;
    color: ${({ theme, actionType, noBg, color }) =>
      noBg
        ? theme.palette.text.secondary
        : getContrastColor(
            darken(
              0.1,
              color ||
                actionButtonColors[
                  actionType as keyof typeof actionButtonColors
                ] ||
                theme.palette.background.default,
            ),
          ) || theme.palette.text.primary}!important;
  }
`;

export const StyledActionButton = styled(Button)<{
  actionType?: string;
  noBg?: boolean;
  showBorder?: boolean;
  size?: "small" | "medium" | "large";
}>`
  ${({ showBorder }) =>
    showBorder &&
    css`
      border-style: 1px solid !important;
    `}
  border-radius: 7px !important;
  height: 25px !important;
  min-width: 25px !important;
  width: unset !important;

  ${({ size }) =>
    size === "small" &&
    css`
      height: 20px !important;
      min-width: 20px !important;
    `}
  ${({ size }) =>
    size === "medium" &&
    css`
      height: 25px !important;
      min-width: 25px !important;
    `}
  ${({ size }) =>
    size === "large" &&
    css`
      height: 30px !important;
      min-width: 30px !important;
    `}
    
  cursor: pointer;
  padding: 5px !important;
  font-size: 13px !important;
  background: unset !important;
  background-color: ${({ theme, actionType, noBg, disabled }) =>
    disabled
      ? theme.palette.background.default
      : noBg
        ? "transparent"
        : actionButtonColors[actionType as keyof typeof actionButtonColors] ||
          theme.palette.background.default}!important;
  border: ${({ actionType }) =>
    actionButtonBorders[actionType as keyof typeof actionButtonBorders] ||
    "none"}!important;
  color: ${({ theme, actionType, noBg }) =>
    noBg
      ? theme.palette.text.primary
      : getContrastColor(
          actionButtonColors[actionType as keyof typeof actionButtonColors] ||
            theme.palette.background.default,
        ) || theme.palette.text.primary}!important;
  ${({ noBg, theme, actionType }) =>
    (noBg ||
      !getContrastColor(
        actionButtonColors[actionType as keyof typeof actionButtonColors] ||
          theme.palette.background.default,
      )) &&
    css`
      box-shadow: none !important;
    `}

  .ant-btn-icon {
    font-size: ${({ size }) =>
      size === "small" ? "16px" : size === "medium" ? "19px" : "22px"};
  }

  &:hover {
    background-color: ${({ theme, actionType, noBg }) =>
      noBg
        ? "transparent"
        : darken(
            0.1,
            actionButtonColors[actionType as keyof typeof actionButtonColors] ||
              theme.palette.background.default,
          )}!important;
    color: ${({ theme, actionType, noBg }) =>
      noBg
        ? theme.palette.text.secondary
        : getContrastColor(
            darken(
              0.1,
              actionButtonColors[
                actionType as keyof typeof actionButtonColors
              ] || theme.palette.background.default,
            ),
          ) || theme.palette.text.primary}!important;
  }
`;
